import { useCallback, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { Row, Col, Button, message, Collapse, Space } from 'antd';
import PatientFormPE from './PatientFormPE';
import { evaluateConditionals } from '../../../services/admission';
import { getServices } from '../../../services/general';

import SymbolsClass from './SymbolsClass';

const reEvaluatePrestations = async (targetPatient, sourcePrestations, form) => {
  const currentPatientData = {
    ...targetPatient,
    ...targetPatient?.generalData,
  };

  const newPrestations = [];
  for (const item of sourcePrestations) {
    let resolvedServiceItem = { ...item };

    if (item.type === 'Collection') {
      try {
        const response = await getServices({
          query: item.name,
          limit: 10,
          branch_office_id: form.getFieldValue('branch_office_id'),
          company_id: form.getFieldValue('company_id'),
          outsourcing_id: form.getFieldValue('outsourcing_id'),
          third_party_id: form.getFieldValue('third_company_id'),
          evaluation_type: targetPatient.evaluation_type,
        });

        const matchingService = response?.data?.find(service => service.id === item.id && service.type === 'Collection');
        if (matchingService) {
          resolvedServiceItem = {
            ...item,
            name: matchingService.name,
            description: matchingService.description || item.description,
            sale_amount: matchingService.sale_amount,
            original_sale_amount: matchingService.sale_amount,
            currency_id: matchingService.currency_id || item.currency_id,
            department_id: matchingService.department_id || item.department_id,
            battery_exams: matchingService.battery_exams || [],
            report_type: matchingService.report_type || null,
            medical_form_type: matchingService.medical_form_type || null,
            observations: matchingService.observations || null,
          };
        }
      } catch (error) {
        console.error('Error fetching updated collection for new evaluation type:', error);
        message.error(`Error actualizando tipo de evaluación para ${item.name}`);
      }
    }

    let removedExamIds = [];
    if (resolvedServiceItem.battery_exams && resolvedServiceItem.battery_exams.length > 0) {
      try {
        const evalResult = await evaluateConditionals({
          patientData: currentPatientData,
          batteryExams: resolvedServiceItem.battery_exams,
          selectedKeywords: []
        });
        removedExamIds = evalResult.removedExamIds || [];
      } catch (err) {
        console.error('Error evaluating conditionals on copy:', err);
        message.error('Error evaluando exámenes condicionales')
      }
    }

    let originalSaleAmount = resolvedServiceItem.original_sale_amount !== undefined ? resolvedServiceItem.original_sale_amount : (resolvedServiceItem.net_amount !== undefined ? resolvedServiceItem.net_amount : resolvedServiceItem.sale_amount);
    let calculatedSaleAmount = originalSaleAmount;

    if ((String(resolvedServiceItem.type).toLowerCase().includes('battery') || String(resolvedServiceItem.type).toLowerCase().includes('collection')) && resolvedServiceItem.battery_exams && resolvedServiceItem.battery_exams.length > 0) {
      let baseExamsMissingPrice = false;
      let baseExamsTotal = 0;
      let removedExamsTotal = 0;
      
      const activeExams = resolvedServiceItem.battery_exams.filter(exam => !removedExamIds.includes(exam.id));
      const removedExams = resolvedServiceItem.battery_exams.filter(exam => removedExamIds.includes(exam.id));
      
      for (const exam of activeExams) {
        if (exam.sale_amount === null || exam.sale_amount === undefined || exam.sale_amount === '') {
          baseExamsMissingPrice = true;
        } else {
          const price = exam.net_amount !== undefined ? parseFloat(exam.net_amount) : parseFloat(exam.sale_amount);
          baseExamsTotal += price || 0;
        }
      }
      
      for (const exam of removedExams) {
        if (exam.sale_amount != null && exam.sale_amount !== '') {
          const price = exam.net_amount !== undefined ? parseFloat(exam.net_amount) : parseFloat(exam.sale_amount);
          removedExamsTotal += price || 0;
        }
      }
      
      calculatedSaleAmount = baseExamsMissingPrice ? ((parseFloat(originalSaleAmount) || 0) - removedExamsTotal) : baseExamsTotal;
    }

    newPrestations.push({
      ...resolvedServiceItem,
      sale_amount: calculatedSaleAmount,
      removedExamIds: removedExamIds,
    });
  }
  return newPrestations;
};

const { Panel } = Collapse;


const PatientsAndServicesPE = ({
  form,
  type,
  patients,
  setPatients,
  totalPatients,
  setTotalPatients,
  countryCode = "PE",
  currentPage,
  setDisabled,
  countrySettings,
  projects,
  setProjects,
  areasList,
  jobPositionsList,
  handleAreaCreated,
  handleJobPositionCreated,
}) => {
  const { t } = useTranslation();

  const checkPatients = useCallback(() => {
    let patientsDiff = totalPatients - patients.length;
    let _patients = patients;

    if (Math.sign(patientsDiff) === 1) {
      for (let i = 0; i < patientsDiff; i++) {
        _patients.push({
          block: null,
          document_number: null,
          document_type: null,
          phone: null,
          profession: null,
          area_id: null,
          job_position_id: null,
          birthdate: null,
          gender: null,
          prestations: []
        });
      }
    }

    if (Math.sign(patientsDiff) === -1) {
      for (let i = 0; i < Math.abs(patientsDiff); i++) {
        _patients.pop();
      }
    }

    setPatients([..._patients]);
  }, [totalPatients]);

  useEffect(() => {
    checkPatients();
  }, [checkPatients]);

  const updatePatientAttribute = (patientIndex, fieldName, value) => {
    const _patients = patients;
    let patient = _patients[patientIndex];

    if (fieldName === 'documentType') {
      patient.document_type = value;
      patient.document_number = '';
      _patients[patientIndex] = patient;
    } else if (!patient.document_type) {
      patient.document_type = 'dni';
    }

    if (fieldName === 'documentNumber') {
      patient.document_number = value;
      _patients[patientIndex] = patient;
    }

    if (fieldName !== 'documentNumber' && fieldName !== 'documentType') {
      _patients[patientIndex][fieldName] = value;
    }

    if (fieldName === 'phone') {
      if (value === null || value === "" || value === "+") {
        _patients[patientIndex][fieldName] = null;
      } else {
        _patients[patientIndex][fieldName] = value;
      }
    }

    setPatients([..._patients]);
  };

  const updatePatientServices = (patientIndex, fieldName, service) => {
    let _patients = patients;
    _patients[patientIndex][fieldName] = service;
    setPatients([..._patients]);
  };

  const updatePatientPrestations = (patientIndex, updatedPrestations) => {
    let _patients = patients;
    _patients[patientIndex]['prestations'] = updatedPrestations;
    setPatients([..._patients]);
  };

  const repeatServicesForAllPatients = async () => {
    let _patients = [...patients];
    const sourcePrestations = _patients[0]['prestations'] || [];
    
    const promises = _patients.map(async (item, idx) => {
      if (idx === 0) return;
      const evaluatedPrestations = await reEvaluatePrestations(item, sourcePrestations, form);
      item['prestations'] = evaluatedPrestations;
    });

    await Promise.all(promises);
    setPatients([..._patients]);
  };

  const repeatLastPatientServices = async (patientIndex) => {
    let _patients = [...patients];
    const sourcePrestations = _patients[patientIndex - 1]["prestations"] || [];
    const evaluatedPrestations = await reEvaluatePrestations(_patients[patientIndex], sourcePrestations, form);
    _patients[patientIndex]["prestations"] = evaluatedPrestations;
    setPatients([..._patients]);
  };

  const addPatient = () => {
    let _patients = patients;

    if (patients.length === 100) {
      message.info({
        content: t("reservations.patientsAndServices.disclaimerPatientMax"),
        style: { marginTop: "40vh" },
      });
      return null;
    }

    _patients.push({
      block: null,
      document_number: null,
      document_type: null,
      phone: null,
      profession: null,
      area_id: null,
      job_position_id: null,
      birthdate: null,
      gender: null,
      prestations: [],
    });
    setPatients([..._patients]);
    setTotalPatients(_patients.length);
  };

  const removePatient = (patientIndex) => {
    let _patients = patients;

    if (patients.length < 2) {
      message.error({
        content: t("reservations.patientsAndServices.disclaimerPatient"),
        style: { marginTop: "30vh", fontSize: 16 },
      });
    } else {
      if (patientIndex > -1) {
        _patients.splice(patientIndex, 1);
      }
      setPatients([..._patients]);
      setTotalPatients(_patients.length);
    }
  };

  const repeatBlocksForAllPatients = () => {
    let _patients = patients;
    _patients.forEach((item) => {
      item['block'] = _patients[0]['block'];
      item['block_id'] = _patients[0]['block_id'];
    });
    setPatients([..._patients]);
  };

  const isReservationSettingsPage = currentPage.includes('editar');

  return (
    <Row
      justify="center"
      className="reservation-settings-wrapper__patients-services mt-15"
    >
      <Col lg={22} xs={22}>
        <Row>
          <h2 className="mb-30">
            {t("reservations.patientsAndServices.title")}
          </h2>
        </Row>
        <Row className="w-100">
          <Collapse
            className="patients-accordion patient-collapse-wrapper"
            expandIconPosition="end"
          >
            {patients?.map((patient, index) => {
              const headerText = (
                <div className="patient-collapse-wrapper__header">
                  <Space align="center" className="patient-collapse-wrapper__header-title">
                    <i className="icon-system-user" />
                    <strong>
                      {patient?.names && patient?.last_name
                        ? `${patient.names} ${patient.last_name.split(' ')[0]}`
                        : `${t("reservations.patientsAndServices.patientTitle")} ${index + 1}`}
                    </strong>
                  </Space>
                  <div className="patient-collapse-wrapper__header-info">
                    {(patient?.document_number) && (
                      <strong>{patient?.document_type ? patient.document_type.toUpperCase() : 'DNI'}: {patient?.document_number}</strong>
                    )}
                  </div>
                </div>
              );

              return (
                <Panel
                  header={headerText}
                  key={index.toString()}
                  className="ant-collapse-item"
                  extra={
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        removePatient(index);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.stopPropagation();
                          removePatient(index);
                        }
                      }}
                      className="patient-collapse-wrapper__actions"
                    >
                      <i className="icon-system-delete" />
                    </span>
                  }
                >
                  <Col
                    key={`${index}-patient`}
                    lg={24}
                    xs={24}
                    className="patient-form-wrapper pt-10 pb-10 pr-20 mb-20"
                  >
                    <Row>
                      <Col lg={isReservationSettingsPage ? 3 : 1} xs={24} className="mt-lg-20">
                        {isReservationSettingsPage && (
                          <div>
                            <Row justify="center">
                              <Col>
                                <p className="title-text mt-30 mr-40">{t("reservations.patientsAndServices.attendanceSubTitle")}</p>
                              </Col>
                            </Row>
                            <Row
                              justify="center"
                              className="symbols-key"
                              key={patient.id}
                            >
                              <Col className={patient.predict_percent === "Sin información" ? "mr-5" : "mr-10"}>
                                {patient.predict_percent === "Sin información" ? <div className="gray-state" />
                                  :
                                  SymbolsClass(patient.predict_percent)}
                              </Col>
                              <Col className={patient.predict_percent === "Sin información" ? "" : "mr-50"}>
                                <p className="text-label-sm">{patient.predict_category && patient.predict_category.charAt(0).toUpperCase() + patient.predict_category.slice(1)}</p>
                              </Col>
                            </Row>
                          </div>
                        )}
                      </Col>
                      <Col lg={isReservationSettingsPage ? 21 : 23} xs={24}>
                        <PatientFormPE
                          reservationForm={form}
                          index={index}
                          patient={patient}
                          updatePatientAttribute={updatePatientAttribute}
                          updatePatientServices={updatePatientServices}
                          updatePatientPrestations={updatePatientPrestations}
                          repeatServicesForAllPatients={repeatServicesForAllPatients}
                          repeatLastPatientServices={repeatLastPatientServices}
                          patients={patients}
                          totalPatients={totalPatients}
                          type={type}
                          countryCode={countryCode}
                          repeatBlocksForAllPatients={repeatBlocksForAllPatients}
                          setDisabled={setDisabled}
                          countrySettings={countrySettings}
                          projects={projects}
                          setProjects={setProjects}
                          areasList={areasList}
                          jobPositionsList={jobPositionsList}
                          handleAreaCreated={handleAreaCreated}
                          handleJobPositionCreated={handleJobPositionCreated}
                        />
                      </Col>
                    </Row>
                  </Col>
                </Panel>
              );
            })}
          </Collapse>
        </Row>
        <Row justify="end" className="mt-10">
          <Col>
            <Button
              onClick={addPatient}
              className="cmt-button secondary outline"
              type="primary"
            >
              <i className="icon-system-plus mr-10" />
              {t("reservations.patientsAndServices.addPatientButton")}
            </Button>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default PatientsAndServicesPE;
